import React, { useState, useEffect } from 'react';
import { userAPI, stravaAPI } from '../services/api';
import Layout from '../components/Layout';
import Modal from '../components/Modal';
import Icon from '../components/Icon';

function ProfilePage({ user, onLogout, darkMode = true, onToggleDarkMode, onUserUpdate }) {
  const [email, setEmail] = useState(user.email || '');
  const [firstname, setFirstname] = useState(user.firstname || '');
  const [surname, setSurname] = useState(user.surname || '');
  const [phone, setPhone] = useState(user.phone || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [annualReminder, setAnnualReminder] = useState(
    user.annual_reminder === undefined || user.annual_reminder === null ? true : Boolean(user.annual_reminder)
  );
  const [marketingReminder, setMarketingReminder] = useState(
    user.marketing_reminder === undefined || user.marketing_reminder === null ? true : Boolean(user.marketing_reminder)
  );
  const [bikeCount, setBikeCount] = useState(user.bike_count || 0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Annual reminder status
  const [reminderStatus, setReminderStatus] = useState([]);
  const [loadingReminders, setLoadingReminders] = useState(false);
  
  // Add bike state
  const [showAddBikeModal, setShowAddBikeModal] = useState(false);
  const [bikeForm, setBikeForm] = useState({
    vin: '',
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    description: ''
  });
  const [addingBike, setAddingBike] = useState(false);

  // Integration states
  const [stravaStatus, setStravaStatus] = useState({ is_connected: false, connection: null });
  const [integrationsLoading, setIntegrationsLoading] = useState(true);

  // Account deletion
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showDeleteSection, setShowDeleteSection] = useState(false);

  // Profile picture
  const [profileImageUrl, setProfileImageUrl] = useState(user.profile_image_url || null);
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);
  const profileImageInputRef = React.useRef(null);

  // Fetch annual reminder status when enabled
  useEffect(() => {
    if (annualReminder && user.role === 'user') {
      fetchReminderStatus();
    }
  }, [annualReminder, user.role]);

  // Fetch integration statuses
  useEffect(() => {
    fetchIntegrationStatuses();
    
    // Check URL params for callback results
    const params = new URLSearchParams(window.location.search);
    if (params.get('strava_connected') === 'true') {
      setSuccess('Strava account connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('strava_error')) {
      const errorType = params.get('strava_error');
      if (errorType === 'limit_exceeded') {
        setError('Error 403: Limit of connected athletes exceeded. This app has exceeded the limit of connected athletes. If you are a developer of this application, please contact Strava developer support for how to request a quota increase.');
      } else {
        setError('Failed to connect Strava account');
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    setAnnualReminder(user.annual_reminder === undefined || user.annual_reminder === null ? true : Boolean(user.annual_reminder));
    setMarketingReminder(user.marketing_reminder === undefined || user.marketing_reminder === null ? true : Boolean(user.marketing_reminder));
    setBikeCount(user.bike_count || 0);
    setProfileImageUrl(user.profile_image_url || null);
  }, [user.annual_reminder, user.marketing_reminder, user.bike_count, user.profile_image_url]);

  // Fetch fresh bike count on mount (fixes stale count after transfer)
  useEffect(() => {
    if (user?.role !== 'super_admin') {
      userAPI.getMyBikes().then((res) => {
        const count = (res.data?.bikes || []).length;
        setBikeCount(count);
        if (onUserUpdate) onUserUpdate({ ...user, bike_count: count });
      }).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchIntegrationStatuses = async () => {
    setIntegrationsLoading(true);
    try {
      const stravaRes = await stravaAPI.getStatus().catch(() => ({ data: { is_connected: false } }));
      setStravaStatus(stravaRes.data);
    } catch (err) {
      console.error('Failed to fetch integration statuses:', err);
    } finally {
      setIntegrationsLoading(false);
    }
  };

  const handleConnectStrava = async () => {
    try {
      const response = await stravaAPI.getAuthUrl();
      window.location.href = response.data.auth_url;
    } catch (err) {
      setError('Failed to initiate Strava connection');
    }
  };

  const handleDisconnectStrava = async () => {
    if (!window.confirm('Disconnect your Strava account?')) return;
    try {
      await stravaAPI.disconnect();
      setStravaStatus({ is_connected: false, connection: null });
      setSuccess('Strava account disconnected');
    } catch (err) {
      setError('Failed to disconnect Strava');
    }
  };

  const fetchReminderStatus = async () => {
    setLoadingReminders(true);
    try {
      const response = await userAPI.getAnnualReminderStatus();
      setReminderStatus(response.data.reminders || []);
    } catch (err) {
      console.error('Failed to load reminder status:', err);
    } finally {
      setLoadingReminders(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword) {
      if (newPassword.length < 6) {
        setError('Password must be at least 6 characters long');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    }

    setLoading(true);

    try {
      const updateData = { 
        email,
        firstname,
        surname,
        phone,
        annual_reminder: annualReminder,
        marketing_reminder: marketingReminder
      };
      if (newPassword) {
        updateData.password = newPassword;
      }

      const response = await userAPI.updateProfile(updateData);
      setSuccess('Profile updated successfully');
      setNewPassword('');
      setConfirmPassword('');
      // Update user in App state
      if (response.data.user && onUserUpdate) {
        onUserUpdate(response.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBike = async (e) => {
    e.preventDefault();
    setError('');
    setAddingBike(true);
    
    try {
      await userAPI.createBike(bikeForm);
      setSuccess('Bike added successfully!');
      setShowAddBikeModal(false);
      setBikeForm({ vin: '', brand: '', model: '', year: new Date().getFullYear(), description: '' });

      let resolvedBikeCount = bikeCount + 1;
      try {
        const bikesResponse = await userAPI.getMyBikes();
        resolvedBikeCount = (bikesResponse.data.bikes || []).length;
      } catch {
        // Keep optimistic count if live refresh fails.
      }

      setBikeCount(resolvedBikeCount);
      if (onUserUpdate) {
        onUserUpdate({ ...user, bike_count: resolvedBikeCount });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add bike');
    } finally {
      setAddingBike(false);
    }
  };

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      setError('Image must be under 3MB');
      return;
    }
    setError('');
    setUploadingProfileImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await userAPI.uploadProfileImage(formData);
      const profileRes = await userAPI.getProfile();
      const newUser = profileRes.data?.user;
      if (newUser?.profile_image_url) {
        setProfileImageUrl(newUser.profile_image_url);
        if (onUserUpdate) onUserUpdate(newUser);
        setSuccess('Profile picture updated');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload profile picture');
    } finally {
      setUploadingProfileImage(false);
      e.target.value = '';
    }
  };

  return (
    <Layout 
      user={user} 
      onLogout={onLogout}
      pageTitle="Profile Settings"
      darkMode={darkMode}
      onToggleDarkMode={onToggleDarkMode}
      pageSubtitle="Manage your account and preferences"
    >
      {error && <div className="error" style={{marginBottom: '20px'}}>{error}</div>}
      {success && <div className="success" style={{marginBottom: '20px'}}>{success}</div>}

      <div
        style={{
          display: 'grid',
          gap: '24px',
          width: '100%',
          maxWidth: '800px',
          margin: '0 auto',
        }}
      >
        {/* Account Information */}
        <div className="card">
          <h2
            style={{
              fontSize: '18px',
              marginBottom: '20px',
              color: darkMode ? 'rgba(255, 255, 255, 0.9)' : 'rgba(30, 30, 45, 1)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Icon name="profile" color="primary" darkMode={darkMode} size={20} />
            Account Information
          </h2>
          {/* Profile picture - for all users */}
          <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid rgba(74, 140, 24, 0.3)'
            }}>
              {profileImageUrl ? (
                <img src={profileImageUrl} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <Icon name="profile" color="muted" darkMode={darkMode} size={36} />
              )}
            </div>
            <div>
              <input
                ref={profileImageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif"
                onChange={handleProfileImageUpload}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => profileImageInputRef.current?.click()}
                disabled={uploadingProfileImage}
                style={{ fontSize: '13px' }}
              >
                {uploadingProfileImage ? 'Uploading...' : (profileImageUrl ? 'Change photo' : 'Add profile photo')}
              </button>
            </div>
          </div>
          <div style={{display: 'grid', gap: '12px'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', padding: '12px', background: 'var(--bg-overlay-light)', borderRadius: 'var(--radius-sm)'}}>
              <span style={{color: 'var(--text-muted)'}}>Username</span>
              <span style={{color: '#ffffff', fontWeight: '500'}}>{user.username}</span>
            </div>
            <div style={{display: 'flex', justifyContent: 'space-between', padding: '12px', background: 'var(--bg-overlay-light)', borderRadius: 'var(--radius-sm)'}}>
              <span style={{color: 'var(--text-muted)'}}>Role</span>
              <span style={{
                padding: '4px 12px',
                background: user.role === 'super_admin' ? 'var(--bg-danger-light)' :
                           user.role === 'company_admin' ? 'rgba(78, 205, 196, 0.2)' :
                           'var(--bg-primary-light)',
                color: user.role === 'super_admin' ? 'var(--color-red)' :
                       user.role === 'company_admin' ? 'var(--color-green)' :
                       'var(--lime-light)',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: '600'
              }}>
                {user.role === 'super_admin' ? 'Super Admin' : 
                 user.role === 'company_admin' ? 'Company Admin' : 'User'}
              </span>
            </div>
            {user.role !== 'super_admin' && (
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: 'var(--bg-overlay-light)', borderRadius: 'var(--radius-sm)'}}>
                <span style={{color: 'var(--text-muted)'}}>Bikes</span>
                <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                  <span style={{color: '#ffffff', fontWeight: '500'}}>{bikeCount}</span>
                  {user.role === 'user' && (
                    <button
                      type="button"
                      onClick={() => setShowAddBikeModal(true)}
                      className="btn btn-sm btn-primary"
                      style={{padding: '6px 12px', fontSize: '12px'}}
                    >
                      + Add Bike
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Update Profile Form */}
        <div className="card">
          <h2 style={{fontSize: '18px', marginBottom: '20px', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px'}}>
            <Icon name="edit" color="primary" darkMode={darkMode} size={20} />
            Update Profile
          </h2>
          
          <form onSubmit={handleSubmit}>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px'}}>
              <div className="form-group" style={{marginBottom: 0}}>
                <label style={{color: 'var(--text-secondary)'}}>First Name</label>
                <input
                  type="text"
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  disabled={loading}
                  placeholder="Your first name"
                />
              </div>

              <div className="form-group" style={{marginBottom: 0}}>
                <label style={{color: 'var(--text-secondary)'}}>Surname</label>
                <input
                  type="text"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  disabled={loading}
                  placeholder="Your surname"
                />
              </div>
            </div>

            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px'}}>
              <div className="form-group" style={{marginBottom: 0}}>
                <label style={{color: 'var(--text-secondary)'}}>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              {user.role !== 'super_admin' && (
                <div className="form-group" style={{marginBottom: 0}}>
                  <label style={{color: 'var(--text-secondary)'}}>Phone Number</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => {
                      // Only allow numbers, spaces, plus, dashes, and parentheses
                      const value = e.target.value.replace(/[^0-9+\-\s()]/g, '');
                      setPhone(value);
                    }}
                    disabled={loading}
                    placeholder="+36 20 123 4567"
                  />
                </div>
              )}
            </div>

            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px'}}>
              <div className="form-group" style={{marginBottom: 0}}>
                <label style={{color: 'var(--text-secondary)'}}>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={loading}
                  placeholder="Enter new password"
                />
              </div>

              <div className="form-group" style={{marginBottom: 0}}>
                <label style={{color: 'var(--text-secondary)'}}>Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  placeholder="Confirm new password"
                />
              </div>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Updating...' : 'Update Profile'}
            </button>
          </form>
        </div>

        {/* Notification Settings - Only for regular users (not business accounts) */}
        {user.role === 'user' && (
          <div className="card">
            <h2 style={{fontSize: '18px', marginBottom: '20px', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px'}}>
              <Icon name="notifications" color="primary" darkMode={darkMode} size={20} />
              Notification Settings
            </h2>
            
            <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '16px',
                background: 'var(--bg-overlay-light)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}>
                <input
                  type="checkbox"
                  checked={annualReminder}
                  onChange={(e) => setAnnualReminder(e.target.checked)}
                  style={{marginTop: '2px', width: '18px', height: '18px', accentColor: 'var(--lime-primary)'}}
                />
                <div>
                  <span style={{color: '#ffffff', fontWeight: '500', display: 'block', marginBottom: '4px'}}>
                    Annual Service Reminder
                  </span>
                  <span style={{color: 'var(--text-muted)', fontSize: '13px'}}>
                    Receive a reminder when your bike is due for its annual service
                  </span>
                </div>
              </label>

              {/* Annual Reminder Status Display */}
              {annualReminder && (
                <div style={{
                  marginTop: '8px',
                  padding: '16px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: 'var(--radius-md)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px',
                    color: '#60a5fa',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}>
                    <Icon name="notifications" color="info" darkMode={darkMode} size={16} />
                    Upcoming Service Reminders
                  </div>
                  
                  {loadingReminders ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Loading...</div>
                  ) : reminderStatus.length === 0 ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No bikes registered yet.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {reminderStatus.map((reminder) => (
                        <div 
                          key={reminder.bike_id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px',
                            background: 'rgba(255,255,255,0.05)',
                            borderRadius: '8px',
                            fontSize: '13px'
                          }}
                        >
                          <div>
                            <span style={{ color: '#fff', fontWeight: '500' }}>
                              {reminder.bike_brand} {reminder.bike_model}
                            </span>
                            <span style={{ color: 'var(--text-muted)', marginLeft: '8px' }}>
                              ({reminder.bike_year})
                            </span>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            {reminder.status === 'no_service' ? (
                              <span style={{ color: 'var(--text-muted)' }}>No service history</span>
                            ) : reminder.status === 'overdue' ? (
                              <span style={{ color: '#f87171', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Icon name="warning" color="error" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle' }} />
                                Service overdue by {Math.abs(reminder.days_until_due)} days
                              </span>
                            ) : reminder.status === 'reminder_sent' || reminder.status === 'upcoming' ? (
                              <span style={{ color: '#fbbf24', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Icon name="calendar" color="warning" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle' }} />
                                Due in {reminder.days_until_due} days
                              </span>
                            ) : (
                              <span style={{ color: '#4A8C18', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Icon name="success" color="success" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle' }} />
                                Next reminder: {new Date(reminder.next_reminder_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '16px',
                background: 'var(--bg-overlay-light)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={marketingReminder}
                  onChange={(e) => setMarketingReminder(e.target.checked)}
                  style={{marginTop: '2px', width: '18px', height: '18px', accentColor: 'var(--lime-primary)'}}
                />
                <div>
                  <span style={{color: '#ffffff', fontWeight: '500', display: 'block', marginBottom: '4px'}}>
                    Marketing Updates
                  </span>
                  <span style={{color: 'var(--text-muted)', fontSize: '13px'}}>
                    Occasional product news and special offers
                  </span>
                </div>
              </label>
            </div>

            <button 
              onClick={handleSubmit} 
              className="btn btn-secondary" 
              disabled={loading}
              style={{marginTop: '20px'}}
            >
              {loading ? 'Saving...' : 'Save Notification Settings'}
            </button>
          </div>
        )}

        {/* Connected Integrations */}
        <div className="card">
          <h2 style={{fontSize: '18px', marginBottom: '20px', color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px'}}>
            <Icon name="integration" color="primary" darkMode={darkMode} size={20} />
            Connected Integrations
          </h2>
          
          {integrationsLoading ? (
            <div style={{ color: 'var(--text-muted)', padding: '20px', textAlign: 'center' }}>
              Loading integrations...
            </div>
          ) : (
            <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
              
              {/* Strava Integration */}
              <div style={{
                padding: '20px',
                background: 'var(--bg-overlay-light)',
                borderRadius: 'var(--radius-md)',
                border: stravaStatus.is_connected ? '1px solid rgba(252, 76, 2, 0.3)' : '1px solid rgba(255,255,255,0.1)'
              }}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #fc4c02 0%, #ff6b35 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px'
                    }}>
                      <Icon name="bike" color="primary" darkMode={false} size={24} />
                    </div>
                    <div>
                      <h3 style={{color: '#ffffff', margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600'}}>
                        Strava
                      </h3>
                      <p style={{color: 'var(--text-muted)', margin: 0, fontSize: '13px'}}>
                        Track your rides and link them to your bike passports
                      </p>
                    </div>
                  </div>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600',
                    background: stravaStatus.is_connected ? 'rgba(34, 197, 94, 0.15)' : 'rgba(255,255,255,0.1)',
                    color: stravaStatus.is_connected ? '#22c55e' : 'var(--text-muted)'
                  }}>
                    {stravaStatus.is_connected ? (
                      <>
                        <Icon name="success" color="success" darkMode={darkMode} size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                        Connected
                      </>
                    ) : 'Not Connected'}
                  </span>
                </div>
                
                {stravaStatus.is_connected && stravaStatus.connection && (
                  <div style={{
                    padding: '12px',
                    background: 'rgba(252, 76, 2, 0.08)',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    fontSize: '13px',
                    color: 'rgba(255,255,255,0.7)'
                  }}>
                    Connected as: <strong style={{color: '#fc4c02'}}>
                      {stravaStatus.connection.athlete_firstname} {stravaStatus.connection.athlete_lastname}
                    </strong>
                  </div>
                )}
                
                <button
                  onClick={stravaStatus.is_connected ? handleDisconnectStrava : handleConnectStrava}
                  className={stravaStatus.is_connected ? 'btn btn-secondary' : 'btn btn-primary'}
                  style={!stravaStatus.is_connected ? {
                    background: 'linear-gradient(135deg, #fc4c02 0%, #ff6b35 100%)',
                    border: 'none'
                  } : {}}
                >
                  {stravaStatus.is_connected ? 'Disconnect' : 'Connect Strava'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Account - Danger zone */}
      {user?.role !== 'super_admin' && (
        <div style={{
          marginTop: '40px',
          padding: '24px',
          background: darkMode ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.06)',
          border: '1px solid rgba(239, 68, 68, 0.25)',
          borderRadius: '16px',
        }}>
          <h3 style={{
            margin: '0 0 12px 0',
            fontSize: '16px',
            color: '#ef4444',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <Icon name="warning" color="error" darkMode={darkMode} size={18} />
            Delete account
          </h3>
          {!showDeleteSection ? (
            <p style={{ margin: 0, fontSize: '14px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', marginBottom: '12px' }}>
              Permanently delete your account. Your bikes and service history will be retained in the system. All personal data (name, email, profile, etc.) will be permanently removed.
            </p>
          ) : (
            <div>
              <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)' }}>
                This action cannot be undone. Your bikes and service history will remain in the database. All other personal data will be permanently deleted.
              </p>
              <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}>
                Type <strong style={{ color: '#ef4444' }}>DELETE</strong> to confirm:
              </p>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE to confirm"
                style={{
                  width: '100%',
                  maxWidth: '280px',
                  padding: '10px 14px',
                  marginBottom: '12px',
                  borderRadius: '8px',
                  border: `1px solid ${deleteConfirmText === 'DELETE' ? 'rgba(239,68,68,0.5)' : (darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)')}`,
                  background: darkMode ? 'rgba(0,0,0,0.2)' : '#fff',
                  color: darkMode ? '#fff' : '#111',
                  fontSize: '14px',
                }}
              />
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={async () => {
                    if (deleteConfirmText !== 'DELETE') return;
                    if (!window.confirm('Are you absolutely sure? This cannot be undone.')) return;
                    try {
                      setDeletingAccount(true);
                      setError('');
                      await userAPI.deleteOwnAccount();
                      window.location.href = '/';
                    } catch (err) {
                      setError(err.response?.data?.error || 'Failed to delete account');
                    } finally {
                      setDeletingAccount(false);
                    }
                  }}
                  disabled={deleteConfirmText !== 'DELETE' || deletingAccount}
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: 600,
                    background: deleteConfirmText === 'DELETE' && !deletingAccount ? '#ef4444' : (darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'),
                    color: '#fff',
                    border: '1px solid rgba(239,68,68,0.4)',
                    borderRadius: '8px',
                    cursor: deleteConfirmText === 'DELETE' && !deletingAccount ? 'pointer' : 'not-allowed',
                    opacity: deleteConfirmText === 'DELETE' && !deletingAccount ? 1 : 0.6,
                  }}
                >
                  {deletingAccount ? 'Deleting...' : 'Permanently delete my account'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowDeleteSection(false); setDeleteConfirmText(''); setError(''); }}
                  className="btn btn-secondary"
                  style={{ padding: '10px 20px', fontSize: '14px' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {!showDeleteSection && (
            <button
              type="button"
              onClick={() => setShowDeleteSection(true)}
              style={{
                marginTop: '8px',
                padding: '8px 16px',
                fontSize: '13px',
                background: 'transparent',
                color: '#ef4444',
                border: '1px solid rgba(239,68,68,0.4)',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              I want to delete my account
            </button>
          )}
        </div>
      )}

      {/* Add Bike Modal */}
      {showAddBikeModal && (
        <Modal onClose={() => {
          setShowAddBikeModal(false);
          setBikeForm({ vin: '', brand: '', model: '', year: new Date().getFullYear(), description: '' });
          setError('');
        }} title="Add New Bike">
          <form onSubmit={handleAddBike}>
            {error && <div className="error" style={{marginBottom: '16px'}}>{error}</div>}
            <div className="form-group">
              <label>Frame Number *</label>
              <input
                type="text"
                value={bikeForm.vin}
                onChange={(e) => setBikeForm({...bikeForm, vin: e.target.value})}
                required
                maxLength={50}
                placeholder="Enter frame number"
                disabled={addingBike}
              />
            </div>
            <div className="form-group">
              <label>Brand *</label>
              <input
                type="text"
                value={bikeForm.brand}
                onChange={(e) => setBikeForm({...bikeForm, brand: e.target.value})}
                required
                placeholder="e.g., Trek, Specialized"
                disabled={addingBike}
              />
            </div>
            <div className="form-group">
              <label>Model *</label>
              <input
                type="text"
                value={bikeForm.model}
                onChange={(e) => setBikeForm({...bikeForm, model: e.target.value})}
                required
                placeholder="e.g., Domane SL 5"
                disabled={addingBike}
              />
            </div>
            <div className="form-group">
              <label>Year *</label>
              <input
                type="number"
                value={bikeForm.year}
                onChange={(e) => setBikeForm({...bikeForm, year: parseInt(e.target.value) || new Date().getFullYear()})}
                required
                min="1900"
                max="2030"
                disabled={addingBike}
              />
            </div>
            <div className="form-group">
              <label>Description (Optional)</label>
              <textarea
                value={bikeForm.description}
                onChange={(e) => setBikeForm({...bikeForm, description: e.target.value})}
                placeholder="Any additional information about your bike..."
                rows={3}
                disabled={addingBike}
              />
            </div>
            <div style={{display: 'flex', gap: '12px', marginTop: '20px'}}>
              <button type="submit" className="btn btn-primary" disabled={addingBike}>
                {addingBike ? 'Adding...' : 'Add Bike'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddBikeModal(false);
                  setBikeForm({ vin: '', brand: '', model: '', year: new Date().getFullYear(), description: '' });
                  setError('');
                }}
                className="btn btn-secondary"
                disabled={addingBike}
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}
    </Layout>
  );
}

export default ProfilePage;
